/**
 * Shared helpers for the new `/v1` backend contract (Phase 0).
 * Legacy Freeze modules must keep using the default `client` + envelope parsing.
 */

/** RFC-7807 problem+json body from Nest. */
export type ProblemJson = {
    type?: string;
    title?: string;
    status?: number;
    detail?: string;
    errors?: unknown;
};

export type Paginated<T> = {
    items: T[];
    total: number;
    page: number;
    pageSize: number;
};

export type StaffRoleType = 'CLERK' | 'MERCHANT_ADMIN' | 'PROVIDER_ADMIN';

export type StaffRoleEntry = {
    parishId: string;
    role: StaffRoleType;
};

const STAFF_ROLE_RANK: Record<StaffRoleType, number> = {
    CLERK: 1,
    MERCHANT_ADMIN: 2,
    PROVIDER_ADMIN: 3,
};

/** Minimum portal staff gate (matches RequireMerchant intent). */
export const MIN_PORTAL_STAFF_ROLE: StaffRoleType = 'CLERK';

export function staffRoleRank(role: StaffRoleType | null | undefined): number {
    if (!role) return 0;
    return STAFF_ROLE_RANK[role] ?? 0;
}

export function hasAtLeastRole(role: StaffRoleType | null | undefined, min: StaffRoleType = MIN_PORTAL_STAFF_ROLE): boolean {
    return staffRoleRank(role) >= STAFF_ROLE_RANK[min];
}

export function effectiveRoleForParish(
    staffRoles: StaffRoleEntry[] | null | undefined,
    parishId: string | null | undefined
): StaffRoleType | null {
    if (!parishId || !staffRoles?.length) return null;
    return staffRoles.find((r) => r.parishId === parishId)?.role ?? null;
}

/**
 * Parish IDs from `/me.staffRoles` where the effective role is ≥ `min`.
 * Uses the same rank helpers as shell gates — does not re-implement inheritance
 * (backend already expands PROVIDER_ADMIN onto descendants in `staffRoles`).
 */
export function parishIdsWithAtLeastRole(
    staffRoles: StaffRoleEntry[] | null | undefined,
    min: StaffRoleType = MIN_PORTAL_STAFF_ROLE
): string[] {
    if (!staffRoles?.length) return [];
    return staffRoles.filter((r) => hasAtLeastRole(r.role, min)).map((r) => r.parishId);
}

/**
 * Zod v4's "this field was absent" message. The backend runs zod 4.4.3, where a missing
 * required field flattens to `Invalid input: expected string, received undefined` — v3's
 * bare `Required` is never emitted. Appended verbatim it reads like noise, so it becomes
 * "is required"; every other message (e.g. `Invalid UUID`) passes through untouched.
 */
const ZOD_MISSING_VALUE = /^Invalid input: expected .+, received (undefined|null)$/;

/**
 * Flatten Nest's `ZodValidationPipe` field errors into one human line.
 * Shape is `Record<string, string[]>` — but `errors` is `unknown` on the wire and other
 * endpoints in this app return other shapes, so anything unrecognised yields null.
 */
function formatFieldErrors(errors: unknown): string | null {
    if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return null;
    const parts = Object.entries(errors as Record<string, unknown>).flatMap(([field, messages]) => {
        if (!Array.isArray(messages)) return [];
        const first = messages.find((m): m is string => typeof m === 'string' && m.length > 0);
        if (!first) return [];
        return [ZOD_MISSING_VALUE.test(first) ? `${field} is required` : `${field}: ${first}`];
    });
    return parts.length ? parts.join(', ') : null;
}

/**
 * HTTP status off an axios-shaped error, or `undefined` when there is no response
 * (network failure, request never sent).
 *
 * Branch on this, never on the `detail` string — problem+json carries prose that is free to be
 * reworded, and a message comparison silently stops matching when it is.
 */
export function getProblemStatus(error: unknown): number | undefined {
    return (error as { response?: { status?: number } })?.response?.status;
}

/**
 * Prefer problem+json `detail`/`title`; never invent fields.
 * Field errors are folded in when present — otherwise a Zod 400 renders as a bare
 * "Validation failed" that names nothing. Applies to every /v1 endpoint, not just one.
 */
export function getProblemDetail(error: unknown): string | null {
    const data = (error as { response?: { data?: ProblemJson } })?.response?.data;
    if (!data || typeof data !== 'object') return null;
    const base = (typeof data.detail === 'string' && data.detail) || (typeof data.title === 'string' && data.title) || null;
    if (!base) return null;
    const fields = formatFieldErrors(data.errors);
    return fields ? `${base}: ${fields}` : base;
}

export function buildPageQuery(page = 1, pageSize = 20, extra?: Record<string, string | number | undefined | null>) {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    if (extra) {
        Object.entries(extra).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') return;
            params.set(key, String(value));
        });
    }
    return params;
}

/**
 * Multipart upload body for `/v1` file fields (`file`).
 * Callers pass the Blob/File; do not cache returned presigned URLs long-term.
 */
export function buildFileFormData(file: Blob, fieldName = 'file'): FormData {
    const form = new FormData();
    form.append(fieldName, file);
    return form;
}
